/**
 * EconomicWebhookSettingsCard — admin card for the e-conomic webhook
 * receiver.
 *
 * Handles the small operational surface for the "push half" of the
 * hybrid sync:
 *   • Enable / disable toggle (auto-subscribes to invoice.booked /
 *     invoice.paid / invoice.overdue on enable).
 *   • Shows the currently-active webhook URL (which contains an
 *     unguessable token). Copy + rotate buttons.
 *   • Displays last-event stats so an admin can verify that events
 *     are actually flowing.
 *
 * Everything else (the daily 04:00 UTC safety-net cron) is unaffected —
 * disabling webhooks only silences the near-real-time path.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Copy, RefreshCw, Webhook, CheckCircle2, XCircle } from 'lucide-react';
import { API_BASE } from '../lib/apiBase';

const API = API_BASE;

export function EconomicWebhookSettingsCard() {
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState(false);

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
    });

    const fetchCfg = async () => {
        try {
            const res = await axios.get(`${API}/api/economic/webhook/config`, {
                headers: authHeaders(),
            });
            setCfg(res.data);
        } catch (e) {
            toast.error('Failed to load webhook config');
        }
    };

    useEffect(() => {
        fetchCfg();
    }, []);

    const toggle = async (checked) => {
        setBusy(true);
        try {
            const path = checked ? 'enable' : 'disable';
            const res = await axios.post(
                `${API}/api/economic/webhook/${path}`,
                {},
                { headers: authHeaders() },
            );
            if (checked) {
                toast.success(
                    `Webhook enabled — ${res.data.created}/${
                        res.data.subscribed_events?.length
                    } subscriptions registered`,
                );
            } else {
                toast.success('Webhook disabled — subscriptions removed');
            }
            await fetchCfg();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Toggle failed');
        } finally {
            setBusy(false);
        }
    };

    const rotate = async () => {
        setBusy(true);
        try {
            await axios.post(
                `${API}/api/economic/webhook/rotate-token`,
                {},
                { headers: authHeaders() },
            );
            toast.success('Token rotated — old URL is now inactive');
            await fetchCfg();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Rotate failed');
        } finally {
            setBusy(false);
        }
    };

    const copyUrl = () => {
        if (!cfg?.webhook_url) return;
        navigator.clipboard.writeText(cfg.webhook_url);
        toast.success('Webhook URL copied');
    };

    if (!cfg) {
        return (
            <div className="border rounded-lg p-4 bg-white text-sm text-slate-400">
                Loading webhook config…
            </div>
        );
    }

    return (
        <div
            className="border rounded-lg p-4 bg-white space-y-3"
            data-testid="economic-webhook-card"
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-md bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                        <Webhook className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-slate-800">
                            e-conomic Webhooks
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5 max-w-md">
                            Push-updates fra e-conomic (booking, betaling, forfald)
                            propageres til Flow inden for få sekunder. Den daglige
                            sync kl. 04:00 UTC kører fortsat som safety net.
                        </p>
                    </div>
                </div>
                <Switch
                    checked={cfg.enabled}
                    disabled={busy}
                    onCheckedChange={toggle}
                    data-testid="economic-webhook-toggle"
                />
            </div>

            {cfg.enabled && cfg.webhook_url && (
                <div className="pt-2 border-t border-slate-100 space-y-2">
                    <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                        Webhook URL (indeholder unikt token)
                    </label>
                    <div className="flex items-center gap-2">
                        <input
                            readOnly
                            value={cfg.webhook_url}
                            className="flex-1 text-[11px] font-mono px-2 py-1.5 border border-slate-200 rounded bg-slate-50 text-slate-700"
                            data-testid="economic-webhook-url"
                        />
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={copyUrl}
                            data-testid="economic-webhook-copy"
                        >
                            <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={rotate}
                            disabled={busy}
                            data-testid="economic-webhook-rotate"
                        >
                            <RefreshCw className="w-3.5 h-3.5" />
                        </Button>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
                        <span>
                            Subscribed:{' '}
                            <span className="font-mono text-slate-700">
                                {cfg.subscribed_events?.join(', ') || 'none'}
                            </span>
                        </span>
                        <span>
                            Aktive subs:{' '}
                            <span className="font-mono text-slate-700">
                                {cfg.active_subscriptions?.length || 0}
                            </span>
                        </span>
                    </div>

                    {cfg.stats?.last_event_at && (
                        <div className="flex items-center gap-2 text-[11px] text-slate-500 pt-1 border-t border-slate-100">
                            {cfg.stats.last_event_ok ? (
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            ) : (
                                <XCircle className="w-3.5 h-3.5 text-rose-500" />
                            )}
                            <span>
                                Sidste event:{' '}
                                <span className="font-mono">
                                    {cfg.stats.last_event_type}
                                </span>{' '}
                                — {new Date(cfg.stats.last_event_at).toLocaleString()}
                            </span>
                            <span className="ml-auto">
                                Modtaget: {cfg.stats.events_received || 0} · Fejlet:{' '}
                                {cfg.stats.events_failed || 0}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default EconomicWebhookSettingsCard;
